import { useState } from "react";
import { Reveal } from "@/components/site/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import { MailOpen } from "lucide-react";

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      trackEvent("generate_lead", { event_category: "contact", source: "home" });
      toast.success("Thank you! Dr. Jain's team will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <section id="notify" className="relative py-28 lg:py-36 max-w-7xl mx-auto px-6 lg:px-10" data-testid="notify">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blush-pale/70 mb-6">
            <MailOpen className="w-4 h-4 text-blush-dusty" />
            <span className="font-sans text-xs tracking-[0.25em] uppercase text-olive-sage">St. Inez, Goa</span>
          </div>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-olive-deep leading-[1.03]">
            We'd love to <span className="italic text-blush-dusty">hear from you.</span>
          </h2>
          <p className="font-sans text-base sm:text-lg text-olive-sage mt-6 max-w-lg leading-relaxed">
            Leave your details and Dr. Jain's team will reach out with appointment availability and
            anything you'd like help with.
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <form onSubmit={submit} data-testid="contact-form"
                className="rounded-3xl bg-white p-8 lg:p-10 border border-blush/30 shadow-[0_20px_60px_rgba(47,51,43,0.08)]">
            <div className="space-y-4">
              <Field label="Full name" value={form.name} onChange={set("name")} testid="contact-name" required />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="contact-email" required />
                <Field label="Phone" value={form.phone} onChange={set("phone")} testid="contact-phone" required />
              </div>
              <div>
                <label className="font-sans text-xs tracking-widest uppercase text-olive-sage">How can we help? (optional)</label>
                <textarea value={form.message} onChange={set("message")} rows={3} data-testid="contact-message"
                  className="mt-1.5 w-full rounded-xl border border-beige bg-cream/50 px-4 py-3 font-sans text-sm outline-none focus:ring-2 focus:ring-olive-sage transition" />
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit"
              className="mt-6 w-full py-4 rounded-full bg-olive-deep text-cream font-sans text-sm hover:bg-blush-dusty transition-colors duration-300 disabled:opacity-60">
              {loading ? "Sending…" : "Send message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="font-sans text-xs tracking-widest uppercase text-olive-sage">{label}</label>
      <input type={type} value={value} onChange={onChange} required={required} data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-beige bg-cream/50 px-4 py-3 font-sans text-sm outline-none focus:ring-2 focus:ring-olive-sage transition" />
    </div>
  );
}
